import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDepressionMode } from '../contexts/DepressionModeContext';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import api from '../api/api';
import { trackABTestEvent } from '../utils/abTesting';
import '../styles/tsumugi-design.css';
import '../styles/pages/RecipeHubPage.css';

/**
 * レシピハブページ
 * つむぎデザインシステム準拠
 * レシピ関連機能への入り口となるハブページ
 */
const RecipeHubPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { depressionMode } = useDepressionMode();
  const [savedRecipesCount, setSavedRecipesCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'レシピ | はしもと栄養パートナー';
    fetchSavedRecipesCount();

    // Track page view
    if (user?._id) {
      trackABTestEvent('RECIPE_NAMING', user._id, 'recipe_hub_view');
    }
  }, [user]);

  const fetchSavedRecipesCount = async () => {
    try {
      const response = await api.get('/api/recipes');
      if (response.data.success) {
        setSavedRecipesCount(response.data.recipes.length);
      }
    } catch (error) {
      console.error('保存レシピ件数取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const recipeActions = [
    {
      id: 'fridge',
      title: '🧊 今ある食材でレシピ',
      description: '買い物不要、疲れた日もすぐ作れる',
      detail: '冷蔵庫を撮影orボタンで食材選択、橋本病に配慮したレシピを提案',
      path: '/fridge-recipe',
      color: 'mint'
    },
    {
      id: 'flyer',
      title: '🛒 特売品でレシピ',
      description: 'お得に栄養バランス',
      detail: '特売品（チラシ・販売棚など）を撮影すると、節約レシピを提案',
      path: '/flyer-recipe',
      color: 'peach'
    },
    {
      id: 'ingredient-check',
      title: '🔍 商品成分表チェック',
      description: '橋本病に優しいか分析',
      detail: '商品の成分表を撮影すると、橋本病への影響を分析します',
      path: '/ingredient-check',
      color: 'lilac'
    }
  ];

  const handleActionClick = (action) => {
    if (action.comingSoon) {
      // 準備中の機能
      return;
    }

    // Track recipe feature click
    if (user?._id) {
      trackABTestEvent('RECIPE_NAMING', user._id, 'recipe_feature_click', {
        featureId: action.id,
        featureName: action.title
      });
    }

    if (action.path) {
      navigate(action.path);
    }
  };

  return (
    <AppLayout>
      <PageTitle
        title="レシピ"
        subtitle="つむぎがあなたに合わせて提案"
        icon="🍽️"
        backPath="/mypage"
        backLabel="マイページ"
        mascot={<img src="/tsumugi.png" alt="つむぎ" className="page-title__mascot-img" />}
      />

      <div className="tsumugi-container recipe-hub">
        {/* おやすみモード時のメッセージ */}
        {depressionMode && (
          <div className="depression-mode-message">
            <span className="depression-mode-message__icon">🌙</span>
            今日は無理しなくていいよ。温めるだけ、混ぜるだけの簡単レシピを優先してね。
          </div>
        )}

        {/* レシピを探すセクション */}
        <section className="recipe-hub__section">
          {!depressionMode && (
            <h2 className="tsumugi-h2 recipe-hub__section-title">
              🔍 レシピを探す
            </h2>
          )}

          <div className="recipe-hub__actions">
            {/* おすすめ: 最初のアクションのみ */}
            <div className={depressionMode ? 'recommended-only' : ''}>
              <button
                key={recipeActions[0].id}
                className={`tsumugi-card recipe-hub__action-card recipe-hub__action-card--${recipeActions[0].color}`}
                onClick={() => handleActionClick(recipeActions[0])}
              >
                <div className="recipe-hub__action-header">
                  <h3 className="tsumugi-h3 recipe-hub__action-title">
                    {recipeActions[0].title}
                  </h3>
                  <p className="tsumugi-body recipe-hub__action-description">
                    {recipeActions[0].description}
                  </p>
                </div>
                {!depressionMode && (
                  <p className="tsumugi-caption recipe-hub__action-detail">
                    {recipeActions[0].detail}
                  </p>
                )}
                <div className="recipe-hub__action-arrow">→</div>
              </button>
            </div>

            {/* その他のアクション */}
            {!depressionMode ? (
              <>
                {recipeActions.slice(1).map((action) => (
                  <button
                    key={action.id}
                    className={`tsumugi-card recipe-hub__action-card recipe-hub__action-card--${action.color} ${action.comingSoon ? 'recipe-hub__action-card--coming-soon' : ''}`}
                    onClick={() => handleActionClick(action)}
                    disabled={action.comingSoon}
                  >
                    {action.comingSoon && (
                      <div className="recipe-hub__coming-soon-badge">準備中</div>
                    )}
                    <div className="recipe-hub__action-header">
                      <h3 className="tsumugi-h3 recipe-hub__action-title">
                        {action.title}
                      </h3>
                      <p className="tsumugi-body recipe-hub__action-description">
                        {action.description}
                      </p>
                    </div>
                    <p className="tsumugi-caption recipe-hub__action-detail">
                      {action.detail}
                    </p>
                    {!action.comingSoon && (
                      <div className="recipe-hub__action-arrow">→</div>
                    )}
                  </button>
                ))}
              </>
            ) : (
              <details className="other-options">
                <summary>他のレシピを見る</summary>
                {recipeActions.slice(1).map((action, index) => (
                  <button
                    key={action.id}
                    className={`tsumugi-card recipe-hub__action-card recipe-hub__action-card--${action.color} ${action.comingSoon ? 'recipe-hub__action-card--coming-soon' : ''} ${index > 0 ? 'recipe-hub__action-card--spaced' : ''}`}
                    onClick={() => handleActionClick(action)}
                    disabled={action.comingSoon}
                  >
                    {action.comingSoon && (
                      <div className="recipe-hub__coming-soon-badge">準備中</div>
                    )}
                    <div className="recipe-hub__action-header">
                      <h3 className="tsumugi-h3 recipe-hub__action-title">
                        {action.title}
                      </h3>
                      <p className="tsumugi-body recipe-hub__action-description">
                        {action.description}
                      </p>
                    </div>
                    {!action.comingSoon && (
                      <div className="recipe-hub__action-arrow">→</div>
                    )}
                  </button>
                ))}
              </details>
            )}
          </div>
        </section>

        {/* 保存済みレシピセクション */}
        <section className="recipe-hub__section">
          <div className="tsumugi-card recipe-hub__saved-card">
            <div className="recipe-hub__saved-header">
              <div>
                <h2 className="tsumugi-h2 recipe-hub__saved-title">
                  💾 保存済みレシピ
                </h2>
                {!loading && !depressionMode && (
                  <p className="tsumugi-body recipe-hub__saved-count">
                    {savedRecipesCount > 0
                      ? `${savedRecipesCount}件のレシピを保存中`
                      : 'まだレシピを保存していません'}
                  </p>
                )}
              </div>
              {savedRecipesCount > 0 && !depressionMode && (
                <div className="recipe-hub__saved-badge">
                  {savedRecipesCount}
                </div>
              )}
            </div>
            {!depressionMode && (
              <p className="tsumugi-caption recipe-hub__saved-description">
                つむぎが提案したレシピや、気に入ったレシピを保存して、いつでも見返せます
              </p>
            )}
            <button
              className="tsumugi-btn tsumugi-btn-primary recipe-hub__saved-btn"
              onClick={() => navigate('/recipes')}
            >
              {depressionMode ? 'レシピを見る' : (savedRecipesCount > 0 ? 'すべてのレシピを見る' : 'レシピを探してみる')}
            </button>
          </div>
        </section>

        {/* レシピ設定セクション - おやすみモードでは非表示 */}
        {!depressionMode && (
          <section className="recipe-hub__section">
            <button
              className="tsumugi-card recipe-hub__settings-card"
              onClick={() => navigate('/seasonings-settings')}
            >
              <div className="recipe-hub__settings-content">
                <div className="recipe-hub__settings-icon">⚙️</div>
                <div className="recipe-hub__settings-text">
                  <h3 className="tsumugi-h3 recipe-hub__settings-title">
                    レシピ設定
                  </h3>
                  <p className="tsumugi-caption recipe-hub__settings-description">
                    調味料・アレルギー・食事制限を設定して、あなたに合ったレシピを
                  </p>
                </div>
                <div className="recipe-hub__settings-arrow">→</div>
              </div>
            </button>
          </section>
        )}

        {/* ヘルプ・ヒント - おやすみモードでは非表示 */}
        {!depressionMode && (
          <section className="recipe-hub__hint">
            <div className="recipe-hub__hint-content">
              <span className="recipe-hub__hint-icon">💡</span>
              <p className="tsumugi-caption recipe-hub__hint-text">
                レシピは橋本病の栄養管理に配慮して作成されています。
                疲れにくい調理法や、甲状腺に優しい食材を優先的に提案します。
              </p>
            </div>
          </section>
        )}
      </div>
    </AppLayout>
  );
};

export default RecipeHubPage;
